import React, { useState } from 'react';
import { X, Repeat2, MapPin, Clock, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GlobalReport } from '../data/mockData';
import { getDisplayName } from '../utils/privacy-utils';
import { toast } from 'sonner';

interface RepostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: GlobalReport | null;
  onRepost: (postId: string, note?: string) => void;
  currentUserName?: string;
}

export const RepostModal: React.FC<RepostModalProps> = ({
  isOpen,
  onClose,
  post,
  onRepost,
  currentUserName
}) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!post) return null;

  const isOwnPost = currentUserName && post.userName === currentUserName;
  const displayName = getDisplayName(post.userId, post.userName, isOwnPost);

  const handleRepost = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onRepost(post.id, note.trim() || undefined);
      toast.success('Reposted!', {
        description: 'Your repost has been shared with your followers',
        icon: '🔄'
      });
      setNote('');
      onClose();
    } catch (error) {
      toast.error('Failed to repost', {
        description: 'Please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-xl bg-white rounded-2xl shadow-2xl z-50 max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat2 className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold text-slate-900">Repost</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 max-h-[calc(90vh-180px)] overflow-y-auto">
              {/* Add your note */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Add your comment (optional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add your thoughts about this report..."
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none resize-none transition-all"
                  rows={3}
                  maxLength={280}
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-500">
                    💡 Tip: Explain why this is important
                  </p>
                  <p className="text-xs text-slate-400">
                    {note.length}/280
                  </p>
                </div>
              </div>

              {/* Original Post Preview */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2">ORIGINAL POST</p>
                <div className="border-2 border-slate-200 rounded-xl p-4 bg-slate-50">
                  {/* Author */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white font-bold">
                      {isOwnPost ? 'Y' : displayName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">
                        {displayName}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>{post.timestamp}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <p className="text-slate-700 text-sm mb-3 line-clamp-4">
                    {post.content}
                  </p>

                  {/* Location */}
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <MapPin className="w-3.5 h-3.5 text-red-500" />
                    <span className="font-medium">
                      {post.location}, {post.region || post.country}
                    </span>
                  </div>

                  {/* Type Badge */}
                  <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-700">
                    {post.type.charAt(0).toUpperCase() + post.type.slice(1)} Report
                  </div>
                </div>
              </div>

              {/* Info Banner */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  <p className="font-medium mb-1">This will:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                    <li>Share this report with your followers</li>
                    <li>Show your name as the reposter</li>
                    <li>Credit the original author</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleRepost}
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Reposting...
                  </>
                ) : (
                  <>
                    <Repeat2 className="w-4 h-4" />
                    Repost
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};